import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '../alert/alert.service';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { ConnectService } from '../connect/connect.service';
import { Device } from '@ionic-native/device/ngx';
import { StorageService } from '../storage/storage.service';
import { TranslateConfigService } from '../translate/translate-config.service';
import { Language } from 'src/app/models/language.model';
import { MixpanelAnalyticsService } from 'src/app/services/mixpanel-analytics/mixpanel-analytics.service';
import { TRACK } from 'src/app/services/mixpanel-analytics/mixpanel.event';
import { lastValueFrom } from 'rxjs';

export interface Auth {
  token: string;
  organization: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public token = '';
  public organization = '';
  public isParticipant = false;

  constructor(
    private alertSvc: AlertService,
    private router: Router,
    private translateSvc: TranslateService,
    private translateConfigService: TranslateConfigService,
    private injector: Injector,
    private device: Device,
    public storageSvc: StorageService,
    private mixpanel: MixpanelAnalyticsService
  ) {}

  public async setSaveToken(token: string) {
    this.token = token;
    await this.storageSvc.set('token', token);
    await this.readToken();
  }

  public async setSaveOrganization(organization: string) {
    this.organization = organization;
    await this.storageSvc.set('organization', organization);
  }

  public async getAuthDetails() {
    const authDetails: Auth = {
      token: await this.readToken(),
      organization: await this.readOrganization(),
    };
    this.token = authDetails.token;
    this.organization = authDetails.organization;

    return authDetails;
  }

  public async readToken() {
    this.token = await this.storageSvc.get('token');
    return this.token;
  }

  public async isLoggedin() {
    await this.getAuthDetails();
    return !!this.token && !!this.organization;
  }

  public async readIsParticipant() {
    const url = await this.storageSvc.get('url');
    this.isParticipant = url === 'participant';
    return this.isParticipant;
  }

  public async readOrganization() {
    this.organization = await this.storageSvc.get('organization');
    return this.organization;
  }

  public async removePushToken() {
    const connectService = this.injector.get(ConnectService);
    connectService.create('/accounts/remove_push_token', {}).subscribe();
  }

  public async logout() {
    const option = await this.alertSvc.presentCustomConfirmation(
      this.translateSvc.instant('AuthSvc.logout'),
      '',
      this.translateSvc.instant('AuthSvc.logoutMessage'),
      this.translateSvc.instant('AuthSvc.no'),
      this.translateSvc.instant('AuthSvc.logout'),
      'logout-confirmation'
    );

    if (option.option) {
      // use injector to prevent dependencies cycle
      this.mixpanel.track(TRACK.LOGOUT, { mixpanelCategory: 'logging_out', mixpanelState: 'complete' });
      this.mixpanel.reset();
      const connectService = this.injector.get(ConnectService);
      await lastValueFrom(connectService.deleteCustom('/accounts/logout', { account: { device_uuid: this.device.uuid } }));
      await this.removeCredentials();
      await this.translateConfigService.resetLanguage();
      this.navigateToRoot();
    }
  }

  async removeCredentials() {
    await this.storageSvc.remove('token');
    await this.storageSvc.remove('organization');
    await this.storageSvc.remove('url');
    this.token = '';
    this.organization = '';
  }

  async setLanguage(language: Language) {
    const locale = await this.translateConfigService.getLanguage(language);
    await this.translateConfigService.setLanguage(locale, true);
  }

  // istanbul ignore next
  navigateToRoot() {
    window.location.href = '';
  }
}
